import type { Aabb, WorldManifest } from "@townsy/contracts";
import type { PlacementRef } from "./decorations";
import { townManifest, worldStreets } from "./districtSlice";
import type { StreetInfo } from "./manifest";
import { streetColliders } from "./queries";
import { streetSeats, type Seat } from "./seats";
import { streetDecor, type StreetDecorPiece } from "./streetDecor";
import { trafficLoops, type LoopPath } from "./traffic";
import { drivableRects } from "./vehicles";

/**
 * World v6 (ADR-0042, founder 2026-10-24: "One shared town"): the whole town as ONE street room. These helpers are what
 * the realtime town street room and the browser (scene, prediction, seats) use: each takes the whole manifest OR any
 * of its district slices and always answers for the whole town (townManifest), so a caller holding a slice cannot get
 * one district's geometry by mistake. They are the street-geometry functions applied to the whole manifest; the server
 * and the browser MUST pass the same owned / fenced sets and yard placements (from PropertyPublic) or prediction drifts.
 */

/** The town's bounds: the union of every district's bounds (the walls keep walkers inside the districts themselves). */
export function townBounds(m: WorldManifest): Aabb {
  const ds = townManifest(m).districts;
  return {
    minX: Math.min(...ds.map((d) => d.bounds.minX)),
    minZ: Math.min(...ds.map((d) => d.bounds.minZ)),
    maxX: Math.max(...ds.map((d) => d.bounds.maxX)),
    maxZ: Math.max(...ds.map((d) => d.bounds.maxZ)),
  };
}

/**
 * Collider boxes of the whole town's street: every district's static colliders (buildings, props, the walls with
 * their openings for Market Street), one box per owned home and the solid street decor (streetColliders of the whole
 * manifest). Add yard items with yardDecorationColliders(m, yard) (it already works on the whole manifest).
 */
export function townColliders(m: WorldManifest, ownedResidentialIds: ReadonlySet<string> = new Set(), fencedPlotIds: ReadonlySet<string> = new Set()): Aabb[] {
  return streetColliders(townManifest(m), ownedResidentialIds, fencedPlotIds);
}

/** Street decor of every plot and billboard of the town (streetDecor of the whole manifest). */
export function townDecor(m: WorldManifest, ownedResidentialIds: ReadonlySet<string> = new Set(), fencedPlotIds: ReadonlySet<string> = new Set()): StreetDecorPiece[] {
  return streetDecor(townManifest(m), ownedResidentialIds, fencedPlotIds);
}

/**
 * Seats of the whole town's street: every district's square benches (Central `bench:<i>:<n>`, the others
 * `bench:<i>:<districtId>:<n>`) and the yard seats of the owned homes anywhere in the town (streetSeats of the whole
 * manifest).
 */
export function townSeats(m: WorldManifest, yard: ReadonlyArray<PlacementRef & { propertyId: string }>): Seat[] {
  return streetSeats(townManifest(m), yard);
}

const DRIVABLE = new WeakMap<WorldManifest, Aabb[]>();
const LOOPS = new WeakMap<WorldManifest, LoopPath[]>();

/**
 * The town's road and crosswalk rects (drivableRects of the whole manifest), one cached array per manifest: pass this
 * same array every step (the vehicles' drivable index is cached by array identity).
 */
export function townDrivableRects(m: WorldManifest): readonly Aabb[] {
  const whole = townManifest(m);
  let rects = DRIVABLE.get(whole);
  if (!rects) {
    rects = drivableRects(whole);
    DRIVABLE.set(whole, rects);
  }
  return rects;
}

/**
 * The town's traffic loops (trafficLoops of the whole manifest, junction zones included), computed once per manifest:
 * cars drive between the districts. Seed them with TOWN_FLEET (= SERVER_FLEET).
 */
export function townTrafficLoops(m: WorldManifest): readonly LoopPath[] {
  const whole = townManifest(m);
  let loops = LOOPS.get(whole);
  if (!loops) {
    loops = trafficLoops(whole);
    LOOPS.set(whole, loops);
  }
  return loops;
}

/** Every named street of the town, district by district in manifest order (worldStreets of each district). */
export function townStreets(m: WorldManifest): readonly StreetInfo[] {
  const whole = townManifest(m);
  return whole.districts.flatMap((d) => worldStreets(whole, d.id));
}
